import { useEffect, useRef, useState } from "react";
import { BrandLogo } from "../components/BrandLogo";

type ListenerStatus = {
  session: { id: number; eventId: number; title: string; eventName: string };
  languages: string[];
  status: "idle" | "live" | "processing" | "ready" | "error";
  sourceTranscript: string;
  translatedTranscript: string;
  summary: string;
  recordingAvailable: boolean;
  recordingUrl: string;
  errorMessage: string;
  sampleRate: number;
};

const languageNames: Record<string, string> = { es: "Español", en: "English", pt: "Português", fr: "Français", it: "Italiano", de: "Deutsch", ja: "日本語", zh: "中文", ko: "한국어", ar: "العربية" };
const speechLocales: Record<string, string> = { es: "es-AR", en: "en-US", pt: "pt-BR", fr: "fr-FR", it: "it-IT", de: "de-DE", ja: "ja-JP", zh: "zh-CN", ko: "ko-KR", ar: "ar-SA" };

function wsUrl(path: string) { return `${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}${path}`; }

type NativeAppBridgeWindow = Window & { ReactNativeWebView?: { postMessage: (message: string) => void } };

export function LiveTranslationListenerPage({ sessionId }: { sessionId: number }) {
  const initialLanguage = new URLSearchParams(location.search).get("idioma") || localStorage.getItem("meetingplanner-listen-language") || navigator.language.split("-")[0] || "es";
  const [language, setLanguage] = useState(initialLanguage);
  const [data, setData] = useState<ListenerStatus | null>(null);
  const [connected, setConnected] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState("");
  const socketRef = useRef<WebSocket | null>(null);
  const contextRef = useRef<AudioContext | null>(null);
  const nextTimeRef = useRef(0);
  const scheduledSourcesRef = useRef(new Set<AudioBufferSourceNode>());
  const playingRef = useRef(false);
  const ttsBufferRef = useRef("");
  const ttsQueueRef = useRef<string[]>([]);
  const ttsSpeakingRef = useRef(false);
  const ttsTimerRef = useRef<number | null>(null);
  const ticket = new URLSearchParams(location.search).get("ticket") || "";
  const ticketQuery = `&ticket=${encodeURIComponent(ticket)}`;
  const appMode = new URLSearchParams(location.search).get("app") === "1";
  const nativeBridge = (window as NativeAppBridgeWindow).ReactNativeWebView;
  const nativeTts = appMode && Boolean(nativeBridge);
  const immediateTts = nativeTts || (appMode && "speechSynthesis" in window);

  async function refresh(target = language) {
    const response = await fetch(`/api/mobile/sessions/${sessionId}/live-translation?language=${encodeURIComponent(target)}${ticketQuery}`, { cache: "no-store" });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || "La sesión no está disponible");
    setData(await response.json());
  }

  function playPcm(arrayBuffer: ArrayBuffer) {
    const context = contextRef.current;
    if (!context || context.state !== "running") return;
    const input = new Int16Array(arrayBuffer);
    const audio = context.createBuffer(1, input.length, 24_000);
    const channel = audio.getChannelData(0);
    for (let index = 0; index < input.length; index += 1) channel[index] = input[index] / 32768;
    const source = context.createBufferSource(); source.buffer = audio; source.connect(context.destination);
    const now = context.currentTime;
    // Un WebSocket puede entregar una ráfaga de PCM más rápido de lo que el
    // dispositivo la reproduce. Nunca dejamos que esa cola aleje al oyente del vivo.
    if (nextTimeRef.current - now > 2.2) {
      for (const pending of scheduledSourcesRef.current) { try { pending.stop(); } catch { /* ya finalizó */ } }
      scheduledSourcesRef.current.clear(); nextTimeRef.current = now + 0.05;
    }
    const start = Math.max(now + 0.04, nextTimeRef.current);
    scheduledSourcesRef.current.add(source);
    source.onended = () => scheduledSourcesRef.current.delete(source);
    source.start(start); nextTimeRef.current = start + audio.duration;
  }

  function pumpTts() {
    if (!immediateTts || ttsSpeakingRef.current || !playingRef.current) return;
    const phrase = ttsQueueRef.current.shift();
    if (!phrase) return;
    if (nativeTts && nativeBridge) {
      nativeBridge.postMessage(JSON.stringify({ type: "translation.tts", text: phrase, language }));
      // El motor nativo mantiene su propia cola de baja latencia.
      ttsSpeakingRef.current = false; pumpTts(); return;
    }
    const utterance = new SpeechSynthesisUtterance(phrase);
    utterance.lang = speechLocales[language] || language;
    utterance.rate = 1.04;
    ttsSpeakingRef.current = true;
    const done = () => { ttsSpeakingRef.current = false; pumpTts(); };
    utterance.onend = done; utterance.onerror = done;
    window.speechSynthesis.speak(utterance);
  }

  function flushTts() {
    if (ttsTimerRef.current !== null) window.clearTimeout(ttsTimerRef.current);
    ttsTimerRef.current = null;
    const phrase = ttsBufferRef.current.trim(); ttsBufferRef.current = "";
    if (!phrase || !playingRef.current) return;
    // Conservamos como máximo una frase pendiente: ante una ráfaga priorizamos
    // lo que está ocurriendo ahora y no una lectura que ya quedó atrasada.
    if (ttsQueueRef.current.length) ttsQueueRef.current.splice(0, ttsQueueRef.current.length);
    ttsQueueRef.current.push(phrase); pumpTts();
  }

  function queueImmediateTts(delta: string) {
    if (!immediateTts || !playingRef.current || !delta) return;
    ttsBufferRef.current += delta;
    if (/[.!?;:\n]\s*$/.test(ttsBufferRef.current) || ttsBufferRef.current.length >= 90) return flushTts();
    if (ttsTimerRef.current !== null) window.clearTimeout(ttsTimerRef.current);
    ttsTimerRef.current = window.setTimeout(flushTts, 420);
  }

  function resetAudioPlayback() {
    playingRef.current = false;
    ttsBufferRef.current = ""; ttsQueueRef.current = []; ttsSpeakingRef.current = false;
    if (ttsTimerRef.current !== null) window.clearTimeout(ttsTimerRef.current);
    ttsTimerRef.current = null;
    if (nativeTts && nativeBridge) nativeBridge.postMessage(JSON.stringify({ type: "translation.tts.stop" }));
    else if (immediateTts) window.speechSynthesis.cancel();
    for (const source of scheduledSourcesRef.current) { try { source.stop(); } catch { /* ya finalizó */ } }
    scheduledSourcesRef.current.clear();
  }

  function connect(target = language) {
    socketRef.current?.close(); setConnected(false); setError("");
    const socket = new WebSocket(wsUrl(`/api/live/sessions/${sessionId}/listen?language=${encodeURIComponent(target)}${ticketQuery}`));
    socket.binaryType = "arraybuffer"; socketRef.current = socket;
    socket.onopen = () => setConnected(true);
    socket.onclose = (event) => { setConnected(false); if (event.code === 4403) { setError("La traducción requiere una detección reciente en la sala de esta sesión."); setData(null); resetAudioPlayback(); } };
    socket.onerror = () => setError("Se perdió la conexión en vivo. La pantalla intentará actualizar el estado.");
    socket.onmessage = (message) => {
      if (message.data instanceof ArrayBuffer) {
        // Dentro de la app usamos la voz local del iPhone desde los deltas de
        // traducción: evita esperar a que el audio remoto termine de generarse.
        if (!immediateTts) playPcm(message.data);
        return;
      }
      try {
        const event = JSON.parse(String(message.data));
        if (event.type === "listener.status") setData(event);
        else if (event.type === "translation.live" || event.type === "translation.ready") {
          setData((current) => current ? { ...current, status: "live", translatedTranscript: "", sourceTranscript: "", summary: "", recordingAvailable: false, recordingUrl: "", errorMessage: "" } : current);
          setError("");
        }
        else if (event.type === "translation.transcript") {
          setData((current) => current ? { ...current, translatedTranscript: event.transcript } : current);
          queueImmediateTts(String(event.delta || ""));
        }
        else if (event.type === "source.transcript") setData((current) => current ? { ...current, sourceTranscript: event.transcript } : current);
        else if (event.type === "translation.processing") { flushTts(); setData((current) => current ? { ...current, status: "processing" } : current); }
        else if (event.type === "translation.ready-archive") setData((current) => current ? { ...current, status: "ready", summary: event.summary, recordingAvailable: Boolean(event.recordingUrl), recordingUrl: event.recordingUrl } : current);
        else if (event.type === "error") setError(event.error);
      } catch { /* mensaje desconocido */ }
    };
  }

  useEffect(() => {
    refresh(language).then(() => connect(language)).catch((reason) => setError(reason.message));
    const timer = window.setInterval(() => refresh(language).catch((reason) => { setError(reason.message); setData(null); socketRef.current?.close(); resetAudioPlayback(); }), 5000);
    return () => { window.clearInterval(timer); socketRef.current?.close(); resetAudioPlayback(); contextRef.current?.close().catch(() => undefined); };
  }, [sessionId, language]);

  async function enableAudio() {
    resetAudioPlayback(); playingRef.current = true;
    if (nativeTts && nativeBridge) {
      nativeBridge.postMessage(JSON.stringify({ type: "translation.tts.ready", language }));
    } else if (immediateTts) {
      // La primera invocación nace del toque del usuario y desbloquea la síntesis
      // de voz de WKWebView/iOS para las frases que llegarán luego por WebSocket.
      const unlock = new SpeechSynthesisUtterance(" "); unlock.volume = 0; unlock.lang = speechLocales[language] || language;
      window.speechSynthesis.speak(unlock);
    } else {
      if (!contextRef.current) contextRef.current = new AudioContext({ sampleRate: 24_000 });
      await contextRef.current.resume(); nextTimeRef.current = contextRef.current.currentTime;
    }
    setPlaying(true);
  }

  function changeLanguage(value: string) {
    resetAudioPlayback(); localStorage.setItem("meetingplanner-listen-language", value); setLanguage(value); setPlaying(false);
    contextRef.current?.close().catch(() => undefined); contextRef.current = null;
  }

  return <div className="listener-page">
    <header className="listener-header"><BrandLogo /><span className={connected ? "connected" : ""}>{connected ? "● Conectado" : "○ Sin transmisión"}</span></header>
    <main className="listener-card">
      <span className="eyebrow">Traducción simultánea</span>
      <h1>{data?.session.title || "Sesión científica"}</h1><p className="listener-event">{data?.session.eventName}</p>
      <label className="field"><span>Escuchar en</span><select value={language} onChange={(event) => changeLanguage(event.target.value)}>{[...new Set([...(data?.languages || []), language])].map((code) => <option value={code} key={code}>{languageNames[code] || code.toUpperCase()}</option>)}</select></label>
      {(data?.status === "idle" || data?.status === "live") && !playing && <button className="button button-primary listener-play" onClick={enableAudio}>▶ {data.status === "idle" ? "Preparar audio traducido" : "Activar audio traducido"}</button>}
      {data?.status === "idle" && playing && <div className="listener-processing">Audio preparado. Esperando que Organize inicie la transmisión…</div>}
      {data?.status === "live" && playing && <div className="listener-live"><i /> {immediateTts ? "Voz inmediata activa" : "Traducción en vivo"} · usá auriculares</div>}
      {data?.status === "processing" && <div className="listener-processing">La sesión finalizó. Estamos preparando el audio y el resumen…</div>}
      {(error || data?.errorMessage) && <div className="inline-error">{error || data?.errorMessage}</div>}
      <section className="listener-subtitles"><span>Subtítulos</span><p>{data?.translatedTranscript || (data?.status === "live" ? "Esperando la voz del expositor…" : "La transmisión todavía no comenzó.")}</p></section>
      {data?.status === "ready" && <section className="listener-archive"><h2>Sesión finalizada</h2>{data.recordingAvailable && data.recordingUrl ? <audio controls preload="metadata" src={`${data.recordingUrl}?ticket=${encodeURIComponent(ticket)}`} /> : <div className="inline-warning">Esta sesión finalizó sin una grabación de audio válida.</div>}<h3>Resumen</h3><p>{data.summary || "El resumen todavía no está disponible."}</p>{data.sourceTranscript && <details><summary>Ver transcripción original</summary><p>{data.sourceTranscript}</p></details>}</section>}
    </main>
  </div>;
}
